<template>
  <!-- Import Modal -->
  <ImportModal
    v-if="showImportModal"
    :title="t('campaignsAutomationList.import.title')"
    import-type="campaign_automation"
    @close="showImportModal = false"
  />

  <!-- Create Campaign Modal -->
  <CampaignAutomationSelectorV2
    v-if="showCreateModal"
    @on-close-dialog="showCreateModal = false"
  />

  <!-- Action Components -->
  <ActivateCampaignAction
    ref="activateRef"
    @confirm="onActionConfirm"
  />
  <DeactivateCampaignAction
    ref="deactivateRef"
    @confirm="onActionConfirm"
  />
  <DuplicateCampaignAction
    ref="duplicateRef"
    @confirm="onActionConfirm"
  />
  <DeleteCampaignAction
    ref="deleteRef"
    @confirm="onActionConfirm"
  />
  <PublishToGalleryDialog
    ref="publishGalleryRef"
    @confirm="onPublishToGalleryConfirm"
  />

  <div class="campaigns-automation-page">
    <PageHeader
      :title="t('campaignsAutomationList.title')"
      :summary="t('campaignsAutomationList.summary')"
    >
      <template #actions>
        <router-link :to="{ name: 'marketing-pressure' }">
          <SpmButton
            :label="t('automatedScenarios.actions.marketingPressure.title')"
            icon="fa fa-gauge-high"
            class-style="p-button p-button-secondary"
          />
        </router-link>
        <SpmButton
          :label="t('automatedScenarios.actions.import')"
          icon="fa fa-file-import"
          class-style="p-button p-button-secondary"
          @click="showImportModal = true"
        />
        <SpmButton
          :label="t('automatedScenarios.actions.create')"
          icon="fa fa-plus"
          class-style="p-button p-button-primary"
          @click="showCreateModal = true"
        />
      </template>
    </PageHeader>

    <!-- Stats period notice -->
    <div class="stats-period-note">
      <i class="fa-light fa-circle-info stats-period-note__icon" />
      <span>{{ t('campaignsAutomationList.statsPeriodNote') }}</span>
    </div>

    <!-- Content Section with DataTable -->
    <div class="page-content">
      <SpmTableAPI
        v-if="apiCall"
        ref="tableRef"
        :api-call="apiCall"
        :columns="columns"
        :show-reload-button="true"
        data-key="id"
        :row-actions-options="rowActions"
        :grouped-actions-options="groupedActions"
        :enable-grouped-actions="true"
        :persistent-filters="persistentFilters"
        :initial-sort="initialSort"
        :show-export-all-button="false"
        :exportable="true"
        expandable
        :export-config="{ entityType: 'campaign_automation' }"
        :lazy-load-fn="loadCampaignsStats"
        :lazy-refresh-interval="120000"
        @export="handleExport"
      >
        <template #empty>
          <SpmEmptyState
            icon="fa-regular fa-robot"
            :title="t('campaignsAutomationList.emptyState.title')"
            :description="t('campaignsAutomationList.emptyState.description')"
            :button-label="t('automatedScenarios.actions.create')"
            button-icon="fa fa-plus"
            @action="showCreateModal = true"
          />
        </template>

        <!-- Name column with meta info -->
        <template #cell-name="{ data }">
          <div class="campaign-info">
            <!-- Title row: integration-owner avatar (prefix) + scenario name.
                 Putting the avatar BEFORE the name mirrors common SaaS
                 patterns (Slack/Discord/Linear) where the source / owner
                 logo leads the row, the title follows. -->
            <div class="campaign-name-line">
              <IntegrationOwnerBadge
                v-if="data.owner_integration"
                :owner-integration="data.owner_integration"
              />
              <span
                v-tooltip.top="data.name"
                class="campaign-name font-bold"
              >{{ data.name }}</span>
            </div>
            <div class="campaign-meta">
              <SpmDateDisplay
                :value="data.date_creation"
                format="datetime"
                icon="fa-light fa-calendar-circle-plus"
                size="sm"
              />
              <SpmDateDisplay
                v-if="data.date_modification"
                :value="data.date_modification"
                format="datetime"
                icon="fa-light fa-edit"
                size="sm"
              />
            </div>
            <div
              v-if="extractChannelTags(data).length || data.track_sales"
              class="campaign-tags"
            >
              <SpmBadge
                v-if="data.track_sales"
                :label="t('campaignsAutomationList.trackSales')"
                icon="fa-solid fa-cart-shopping"
                :colors="trackSalesBadgeStyle"
                size="sm"
              />
              <SpmBadge
                v-for="channel in extractChannelTags(data)"
                :key="`${data.id}-${channel}`"
                :label="formatChannel(channel)"
                :icon="getChannelIcon(channel)"
                :colors="getChannelTagStyle(channel)"
                size="sm"
              />
            </div>
          </div>
        </template>

        <!-- Status column -->
        <template #cell-status="{ data }">
          <SpmBadge
            :label="t(`campaignsAutomationList.status.${data.status}`)"
            :icon="getStatusIcon(data.status)"
            :colors="getStatusStyle(data.status)"
          />
        </template>

        <!-- Performance column -->
        <template #cell-performance="{ data }">
          <div class="metrics-display">
            <SpmMetricDisplay
              :label="t('campaignsAutomationList.metrics.openRate')"
              :help="t('campaigns.common.metrics.openRateUniqueHelp')"
              :value="data.open_rate"
              type="percentage"
              :is-decimal="false"
            />
            <SpmMetricDisplay
              :label="t('campaigns.common.metrics.openRateTotal')"
              :help="t('campaigns.common.metrics.openRateTotalHelp')"
              :value="data.open_rate_total"
              type="percentage"
              :is-decimal="false"
            />
            <SpmMetricDisplay
              :label="t('campaignsAutomationList.metrics.clickRate')"
              :value="data.click_rate"
              type="percentage"
              :is-decimal="false"
            />
          </div>
        </template>

        <!-- Conversions column -->
        <template #cell-conversions="{ data }">
          <div class="metrics-display">
            <SpmMetricDisplay
              :label="t('campaignsAutomationList.metrics.salesAmount')"
              :value="data.sales_amount"
              type="currency"
            />
            <SpmMetricDisplay
              :label="t('campaignsAutomationList.metrics.conversionRate')"
              :value="data.conversion_rate"
              type="percentage"
              :is-decimal="false"
            />
          </div>
        </template>

        <!-- Expansion row with detailed metrics -->
        <template #expansion="{ data }">
          <div class="expansion-container">
            <!-- Summary Cards Row -->
            <div class="summary-cards">
              <div class="summary-card summary-card--primary">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-paper-plane" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatInteger(data.deliveries) }}</span>
                  <span class="summary-card__label">{{ t('campaignsAutomationList.expansion.deliveries') }}</span>
                </div>
              </div>
              <div class="summary-card summary-card--success">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-envelope-open" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatPercentage(data.open_rate) }}</span>
                  <span class="summary-card__label">
                    {{ t('campaignsAutomationList.metrics.openRate') }}
                    <SpmHelpIcon :text="t('campaigns.common.metrics.openRateUniqueHelp')" />
                  </span>
                </div>
              </div>
              <div class="summary-card summary-card--success">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-envelope-open-text" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatPercentage(data.open_rate_total) }}</span>
                  <span class="summary-card__label">
                    {{ t('campaigns.common.metrics.openRateTotal') }}
                    <SpmHelpIcon :text="t('campaigns.common.metrics.openRateTotalHelp')" />
                  </span>
                </div>
              </div>
              <div class="summary-card summary-card--info">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-mouse-pointer" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatPercentage(data.click_rate) }}</span>
                  <span class="summary-card__label">{{ t('campaignsAutomationList.metrics.clickRate') }}</span>
                </div>
              </div>
              <div class="summary-card summary-card--warning">
                <div class="summary-card__icon">
                  <i class="fa-solid fa-shopping-cart" />
                </div>
                <div class="summary-card__content">
                  <span class="summary-card__value">{{ formatAmount(data.sales_amount) }}</span>
                  <span class="summary-card__label">{{ t('campaignsAutomationList.metrics.salesAmount') }}</span>
                </div>
              </div>
            </div>

            <!-- Compact Metrics Row -->
            <div class="metrics-row">
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsAutomationList.expansion.opens') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.opens) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsAutomationList.expansion.openUnique') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.open_unique) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsAutomationList.expansion.clicks') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.clicks) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsAutomationList.expansion.clickUnique') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.click_unique) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsAutomationList.expansion.sales') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.sales) }}</span>
              </div>
              <div class="metric-item metric-item--warning">
                <span class="metric-item__label">{{ t('campaignsAutomationList.expansion.bounces') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.bounces) }}</span>
              </div>
              <div class="metric-item metric-item--danger">
                <span class="metric-item__label">{{ t('campaignsAutomationList.expansion.spam') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.spam) }}</span>
              </div>
              <div class="metric-item">
                <span class="metric-item__label">{{ t('campaignsAutomationList.expansion.unsubscribes') }}</span>
                <span class="metric-item__value">{{ formatInteger(data.unsubscribes) }}</span>
              </div>
            </div>

            <!-- Channel Stats (inline display) -->
            <div
              v-if="data.channels?.length"
              class="channels-inline"
            >
              <div
                v-for="channel in data.channels"
                :key="`${data.id}-${channel.channel}`"
                class="channel-inline"
              >
                <div class="channel-inline__header">
                  <i
                    v-if="getChannelIcon(channel.channel)"
                    :class="['channel-inline__icon', getChannelIcon(channel.channel)]"
                  />
                  <span class="channel-inline__name">{{ formatChannel(channel.channel) }}</span>
                </div>
                <div class="channel-inline__stats">
                  <span class="channel-inline__stat">
                    {{ t('campaignsAutomationList.expansion.deliveries') }}: <strong>{{ formatInteger(channel.deliveries) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaignsAutomationList.metrics.openRate') }}: <strong class="highlight">{{ formatPercentage(channel.open_rate) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaigns.common.metrics.openRateTotal') }}: <strong class="highlight">{{ formatPercentage(channel.open_rate_total) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaignsAutomationList.metrics.clickRate') }}: <strong class="highlight">{{ formatPercentage(channel.click_rate) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaignsAutomationList.metrics.salesAmount') }}: <strong>{{ formatAmount(channel.sales_amount) }}</strong>
                  </span>
                  <span class="channel-inline__stat">
                    {{ t('campaignsAutomationList.expansion.revenuePerSend') }}: <strong>{{ formatAmount(channel.revenue_per_send) }}</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </SpmTableAPI>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import { useStore } from '@/store';
import { CampaignAutomationStatusEnum } from '@/types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import { showToastSuccess, showToastError } from '@/helpers';
import {
  getChannelIcon,
  getChannelTagStyleVars,
  getStatusIcon,
  getStatusStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmMetricDisplay from '@/components/table-rest-api/SpmMetricDisplay.vue';
import SpmHelpIcon from '@/components/table-rest-api/SpmHelpIcon.vue';
import {
  formatPercentage as formatPercentageUtil,
  formatCurrency,
  formatInteger as formatIntegerUtil,
} from '@/components/table-rest-api/formatters';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  SpmActiveSort,
  ExportPayload,
  SpmTableAPIExposed,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import { isDemoModeActive, demoCampaigns } from '@/composables/DemoMode';
import ImportModal from '@/components/import/ImportModal.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmEmptyState from '@/components/table-rest-api/SpmEmptyState.vue';
import CampaignAutomationSelectorV2 from '@/components/automated-scenarios/CampaignAutomationSelectorV2.vue';
import IntegrationOwnerBadge from '@/components/integrations/IntegrationOwnerBadge.vue';
import {
  ActivateCampaignAction,
  DeactivateCampaignAction,
  DuplicateCampaignAction,
  DeleteCampaignAction,
  useAutomationCampaignsActions,
} from './AutomationCampaignsActions';
import type { AutomationCampaignData } from './AutomationCampaignsActions/types';
import { PublishToGalleryDialog } from './BulkCampaignsActions';
import { updateAutomationTrackSales } from '@/composables/flows/useFlowApi';

export default defineComponent({
  name: 'CampaignsAutomationList',

  components: {
    SpmTableAPI,
    PageHeader,
    SpmBadge,
    SpmDateDisplay,
    SpmMetricDisplay,
    SpmHelpIcon,
    ImportModal,
    SpmButton,
    SpmEmptyState,
    CampaignAutomationSelectorV2,
    IntegrationOwnerBadge,
    ActivateCampaignAction,
    DeactivateCampaignAction,
    DuplicateCampaignAction,
    DeleteCampaignAction,
    PublishToGalleryDialog,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const tableRef = ref<SpmTableAPIExposed | null>(null);
    const showImportModal = ref(false);
    const showCreateModal = ref(false);

    // Watch flow editor visibility to reload table when editor closes
    const isFlowEditorVisible = computed(() => store.getters['flowEditor/isEditorVisible']);
    watch(isFlowEditorVisible, (newValue, oldValue) => {
      // Reload table when editor closes (visible -> hidden)
      if (oldValue === true && newValue === false && tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    });

    // Watch import modal visibility to reload table when editor closes
    watch(showImportModal, (newValue, oldValue) => {
      // Reload table when editor closes (visible -> hidden)
      if (oldValue === true && newValue === false && tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    });

    // Action component refs
    const activateRef = ref(null);
    const deactivateRef = ref(null);
    const duplicateRef = ref(null);
    const deleteRef = ref(null);
    const publishGalleryRef = ref<{ open: (list: { id: number; name: string }[]) => void; done: () => void } | null>(null);

    // Hidden statuses (not shown in the list)
    const hiddenStatuses: CampaignAutomationStatusEnum[] = [
      CampaignAutomationStatusEnum.ARCHIVED,
      CampaignAutomationStatusEnum.DELETED,
    ];

    // Status options for filter (all except hidden statuses)
    const statusOptions = computed(() => (Object.values(CampaignAutomationStatusEnum) as CampaignAutomationStatusEnum[])
      .filter((status) => !hiddenStatuses.includes(status))
      .map((status) => ({
        label: t(`campaignsAutomationList.status.${status}`),
        value: status,
      })));

    // API call configuration.
    // `withStats=false` : le listing revient sans aucune métrique (rapide) ;
    // les colonnes `lazy` sont remplies ensuite via `loadCampaignsStats`.
    const apiCall = ref<ApiCallConfig>({
      type_api: NestApiType.CLIENT_API,
      path: '/campaigns/automations?withStats=false',
      secure: true,
    });

    /**
     * Stats différées des colonnes lazy : un appel scopé sur les ids du lot,
     * réponse indexée par clé de ligne pour le merge dans le tableau.
     */
    const loadCampaignsStats = async (
      rows: Record<string, unknown>[],
    ): Promise<Record<string, Record<string, unknown>> | null> => {
      const ids = rows.map((row) => row.id).filter(Boolean);
      if (!ids.length) {
        return null;
      }

      const response = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        '/campaigns/automations/stats',
        {},
        '',
        undefined,
        true,
        { ids: ids.join(',') },
      );

      if (!response?.data || !Array.isArray(response.data)) {
        return null;
      }

      return Object.fromEntries(
        response.data.map(
          (stats: Record<string, unknown>): [string, Record<string, unknown>] => [String(stats.id), stats],
        ),
      );
    };

    // Persistent filters - exclude hidden statuses + demo mode filter
    const persistentFilters = computed<SpmApiFilter[]>(() => {
      const filters = hiddenStatuses.map((status) => ({
        field: 'status',
        operator: SpmFilterOperator.NOT_EQUALS,
        values: status,
      }));

      // In demo mode, only show selected campaigns (or none if empty)
      if (isDemoModeActive.value) {
        const ids = demoCampaigns.automationIds;
        filters.push({
          field: 'id',
          operator: SpmFilterOperator.IN,
          values: (ids.length > 0 ? ids.join(',') : '0') as any,
        });
      }

      return filters;
    });

    // Initial sort - most recent first
    const initialSort: SpmActiveSort = {
      field: 'date_modification',
      order: 'desc',
    };

    // Columns configuration
    const columns = computed<SpmColumnConfig[]>(() => [
      {
        id: 'name',
        header: t('campaignsAutomationList.columns.name'),
        maxWidth: '35%',
        fields: [
          {
            name: 'name',
            label: t('campaignsAutomationList.columns.name'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'textInput' },
          },
          {
            name: 'date_creation',
            label: t('campaignsAutomationList.columns.createdAt'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
          {
            name: 'date_modification',
            label: t('campaignsAutomationList.columns.modifiedAt'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
          'primary_channel',
          'channels',
        ],
      },
      {
        id: 'status',
        header: t('campaignsAutomationList.columns.status'),
        fields: [
          {
            name: 'status',
            label: t('campaignsAutomationList.columns.status'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'singleSelect',
              options: statusOptions.value,
            },
          },
        ],
      },
      {
        id: 'performance',
        lazy: true,
        header: t('campaignsAutomationList.columns.performance'),
        fields: [
          {
            name: 'open_rate',
            label: t('campaignsAutomationList.metrics.openRate'),
            sortable: true,
          },
          {
            name: 'open_rate_total',
            label: t('campaigns.common.metrics.openRateTotal'),
            sortable: true,
          },
          {
            name: 'click_rate',
            label: t('campaignsAutomationList.metrics.clickRate'),
            sortable: true,
          },
          'deliveries',
          'opens',
          'open_unique',
          'clicks',
          'click_unique',
        ],
      },
      {
        id: 'conversions',
        lazy: true,
        header: t('campaignsAutomationList.columns.conversions'),
        fields: [
          {
            name: 'sales_amount',
            label: t('campaignsAutomationList.metrics.salesAmount'),
            sortable: true,
          },
          {
            name: 'conversion_rate',
            label: t('campaignsAutomationList.metrics.conversionRate'),
            sortable: true,
          },
          'sales',
          'bounces',
          'spam',
          'unsubscribes',
          'revenue_per_send',
        ],
      },
    ]);

    // Use the composable for actions
    const { rowActions, groupedActions } = useAutomationCampaignsActions({
      activateRef,
      deactivateRef,
      duplicateRef,
      deleteRef,
      onToggleTrackSales: async (campaign, trackSales) => {
        try {
          setLoading(true);
          await updateAutomationTrackSales(campaign.id, trackSales);
          await showToastSuccess(
            t(
              trackSales
                ? 'campaignsAutomationList.actions.trackSalesSuccess'
                : 'campaignsAutomationList.actions.untrackSalesSuccess',
            ),
          );
          reloadTableData();
        } catch (error) {
          console.error('Toggle track_sales error:', error);
          await showToastError(t('campaignsAutomationList.actions.trackSalesError'));
        } finally {
          setLoading(false);
        }
      },
      onPublishToGallery: async (selection) => {
        if (selection.selectionCount === 0) return;

        try {
          setLoading(true);
          // Build query params based on selection mode
          const params: Record<string, any> = {
            fields: ['id', 'name'],
            limit: selection.selectionCount,
          };

          if (selection.selectAllMode) {
            if (selection.excludedRows.length > 0) {
              params.filters = [{ field: 'id', operator: 'not_in', values: selection.excludedRows.join(',') }];
            }
          } else {
            params.filters = [{ field: 'id', operator: 'in', values: selection.selectedRows.join(',') }];
          }

          const response = await nestCall(
            'get',
            NestApiType.CLIENT_API,
            '/campaigns/automations',
            {},
            '',
            undefined,
            true,
            params,
          );
          const campaignList = (response?.data || []).map((c: any) => ({ id: Number(c.id), name: c.name }));
          if (campaignList.length > 0 && publishGalleryRef.value?.open) {
            publishGalleryRef.value.open(campaignList);
          }
        } catch (error) {
          console.error('Publish to gallery error:', error);
          await showToastError(t('campaignsAutomationList.groupedActions.publishToGalleryError'));
        } finally {
          setLoading(false);
        }
      },
    });

    /**
     * Reset table selection after a grouped action is confirmed
     */
    const resetTableSelection = () => {
      if (tableRef.value?.unselectAll) {
        tableRef.value.unselectAll();
      }
    };

    /**
     * Reload table data after an action
     */
    const reloadTableData = () => {
      if (tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    };

    // Handler for action confirmations (table reload)
    const onActionConfirm = () => {
      resetTableSelection();
      reloadTableData();
    };

    // Handler for publish to gallery dialog confirmation
    const onPublishToGalleryConfirm = async (payload: { campaignIds: number[]; campaignIdsWithoutTemplates: number[] }) => {
      try {
        setLoading(true);
        const response = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/data/publish-to-gallery',
          {},
          '',
          {
            shopId: UserState.activeShop?.id,
            campaignIds: payload.campaignIds,
            campaignIdsWithoutTemplates: payload.campaignIdsWithoutTemplates,
            type: 'campaign_automation',
          },
          true,
        );
        if (response?.success) {
          await showToastSuccess(t('campaignsAutomationList.groupedActions.publishToGallerySuccess'));
        } else {
          await showToastError(response?.message || t('campaignsAutomationList.groupedActions.publishToGalleryError'));
        }
      } catch (error) {
        console.error('Publish to gallery error:', error);
        await showToastError(t('campaignsAutomationList.groupedActions.publishToGalleryError'));
      } finally {
        if (publishGalleryRef.value?.done) {
          publishGalleryRef.value.done();
        }
        resetTableSelection();
        setLoading(false);
      }
    };

    const setLoading = async (loading: boolean) => {
      if (tableRef.value) {
        await tableRef.value.setLoading(loading);
      }
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );
        if (response && !response.error) {
          await showToastSuccess(t('campaignsAutomationList.export.success'));
        } else {
          await showToastSuccess(t('campaignsAutomationList.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      } finally {
        resetTableSelection();
        reloadTableData();
        setLoading(false);
      }
    };

    // Formatting functions - using shared formatters
    const formatPercentage = (value: number | null | undefined): string => formatPercentageUtil(value, false);

    const formatAmount = (value: number | null | undefined): string => formatCurrency(value, 'EUR');

    const formatInteger = (value: number | null | undefined): string => formatIntegerUtil(value);

    const formatChannel = (value: string | null): string => {
      if (!value) return '-';
      const key = value.toLowerCase();
      const translationKey = `campaignsAutomationList.channels.${key}`;
      const translated = t(translationKey);
      return translated === translationKey ? key : translated;
    };

    const extractChannelTags = (row: AutomationCampaignData): string[] => {
      const uniqueChannels = new Set<string>();

      if (Array.isArray(row?.channels)) {
        row.channels.forEach((channel) => {
          if (channel?.channel) {
            uniqueChannels.add(channel.channel);
          }
        });
      }

      if (row?.primary_channel) {
        uniqueChannels.add(row.primary_channel);
      }

      return Array.from(uniqueChannels);
    };

    // Style functions - convert CSS vars format to SpmBadge format
    const getStatusStyle = (status: string | null | undefined) => {
      const vars = getStatusStyleVars(status);
      return {
        bg: vars['--status-bg'] || 'var(--surface-200)',
        color: vars['--status-color'] || 'var(--text-color)',
        borderColor: vars['--status-border'],
      };
    };
    const trackSalesBadgeStyle = {
      bg: 'rgba(34, 197, 94, 0.12)',
      color: '#16a34a',
      borderColor: 'rgba(34, 197, 94, 0.3)',
    };

    const getChannelTagStyle = (channel: string | null | undefined) => {
      const vars = getChannelTagStyleVars(channel);
      return {
        bg: vars['--channel-bg'] || 'var(--surface-200)',
        color: vars['--channel-color'] || 'var(--text-color)',
        borderColor: vars['--channel-border'],
      };
    };

    return {
      t,
      tableRef,
      apiCall,
      loadCampaignsStats,
      columns,
      persistentFilters,
      initialSort,
      rowActions,
      groupedActions,
      showImportModal,
      showCreateModal,
      // Action component refs
      activateRef,
      deactivateRef,
      duplicateRef,
      deleteRef,
      publishGalleryRef,
      onActionConfirm,
      onPublishToGalleryConfirm,
      // Formatting functions
      formatPercentage,
      formatAmount,
      formatInteger,
      formatChannel,
      extractChannelTags,
      // Style functions
      getStatusStyle,
      getStatusIcon,
      getChannelTagStyle,
      getChannelIcon,
      trackSalesBadgeStyle,
      handleExport,
    };
  },
});
</script>

<style lang="scss" scoped>
/* Page layout */
.campaigns-automation-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Stats period notice */
.stats-period-note {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem;
  padding: 0.625rem 0.875rem;
  font-size: 0.8125rem;
  color: var(--text-color-secondary);
  background: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 8px;

  &__icon {
    color: var(--primary-color);
    font-size: 0.95rem;
  }
}

/* Campaign info styling */
.campaign-info {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

/* Single line: scenario name + integration owner avatar side-by-side.
   The name takes all available room (with ellipsis on overflow) so the
   avatar stays anchored on the right. */
.campaign-name-line {
  display: flex;
  align-items: center;
  min-width: 0;
}

.campaign-name {
  font-weight: 500;
  color: var(--text-color);
  display: block;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.campaign-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

/* Channel tags */
.campaign-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.25rem;
}

/* Metrics display */
.metrics-display {
  display: grid;
  grid-template-columns: max-content max-content;
  align-items: center;
  gap: 0.375rem 0.5rem;

  // Labels and values join the grid so every value lines up after the longest label.
  :deep(.spm-metric-display) {
    display: contents;
  }
}

/* Expansion Container */
.expansion-container {
  padding: 1.25rem;
  background: linear-gradient(135deg, var(--surface-50) 0%, var(--surface-100) 100%);
  border-radius: 8px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--surface-0);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--surface-200);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.summary-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  font-size: 1.25rem;
}

.summary-card--primary .summary-card__icon {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
}

.summary-card--success .summary-card__icon {
  background: rgba(34, 197, 94, 0.12);
  color: #22c55e;
}

.summary-card--info .summary-card__icon {
  background: rgba(14, 165, 233, 0.12);
  color: #0ea5e9;
}

.summary-card--warning .summary-card__icon {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.summary-card__content {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.summary-card__value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
  line-height: 1.2;
}

.summary-card__label {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* Compact Metrics Row */
.metrics-row {
  display: flex;
  gap: 0.5rem;
}

.metric-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-200);
  border-radius: 4px;
}

.metric-item__label {
  font-size: 0.6rem;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  text-align: center;
  line-height: 1.2;
  margin-bottom: 0.25rem;
}

.metric-item__value {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color);
}

.metric-item--warning .metric-item__value {
  color: #f59e0b;
}

.metric-item--danger .metric-item__value {
  color: #ef4444;
}

/* Channels Inline Display */
.channels-inline {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.channel-inline {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0.75rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-200);
  border-radius: 4px;
}

.channel-inline__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 100px;
}

.channel-inline__icon {
  font-size: 0.875rem;
  color: var(--primary-color);
}

.channel-inline__name {
  font-weight: 600;
  font-size: 0.8rem;
  color: var(--text-color);
}

.channel-inline__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.channel-inline__stat {
  font-size: 0.75rem;
  color: var(--text-color-secondary);

  strong {
    color: var(--text-color);
    font-weight: 600;

    &.highlight {
      color: var(--primary-color);
    }
  }
}
</style>
